import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { sessionService } from '../../services/session.service';
import { Session } from '../../types/session.types';
import { useNotification } from '../../context/NotificationContext';

const statusChipConfig: Record<string, { label: string; color: 'default' | 'success' | 'info' | 'warning' }> = {
  scheduled: { label: 'Scheduled', color: 'info' },
  preparing: { label: 'Provisioning', color: 'warning' },
  ready: { label: 'Ready', color: 'success' },
  active: { label: 'Active', color: 'success' },
  completed: { label: 'Completed', color: 'default' },
};

const filterTabs = ['all', 'scheduled', 'active', 'completed'];

const SessionList: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<Session | null>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const data = await sessionService.getSessions();
      setSessions(data);
    } catch {
      showNotificationRef.current('Failed to load sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const deletableStatuses = ['scheduled', 'completed', 'cancelled'];

  const handleDelete = async () => {
    if (!sessionToDelete) return;
    setDeleteDialogOpen(false);
    try {
      await sessionService.deleteSession(sessionToDelete.id);
      showNotificationRef.current('Session deleted', 'success');
      await loadSessions();
    } catch {
      showNotificationRef.current('Failed to delete session', 'error');
    }
    setSessionToDelete(null);
  };

  const filteredSessions =
    filterTab === 0
      ? sessions
      : sessions.filter((s) => s.status === filterTabs[filterTab]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Sessions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/instructor/sessions/new')}
        >
          Create Session
        </Button>
      </Box>

      {/* Filter Tabs */}
      <Tabs
        value={filterTab}
        onChange={(_, v) => setFilterTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab label="All" />
        <Tab label="Scheduled" />
        <Tab label="Active" />
        <Tab label="Completed" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredSessions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {filterTab === 0
              ? 'No sessions yet. Create a session from a published tech eval.'
              : `No ${filterTabs[filterTab]} sessions.`}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Session Name</TableCell>
                <TableCell>Tech Eval</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scheduled Date</TableCell>
                <TableCell>Students</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSessions.map((s) => (
                <TableRow
                  key={s.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/instructor/session/${s.id}`)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {s.name}
                      </Typography>
                      {s.role === 'co-instructor' && (
                        <Chip label="Co-instructor" size="small" variant="outlined" color="info" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {s.workshop?.title || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusChipConfig[s.status]?.label || s.status}
                      color={statusChipConfig[s.status]?.color || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {s.scheduledStart ? dayjs(s.scheduledStart).format('MMM D, YYYY h:mm A') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {s.studentCount ?? 0}
                    </Typography>
                    {s.status === 'completed' && s.feedbackCount !== undefined && s.feedbackCount > 0 && (
                      <Typography variant="caption" color="info.main" sx={{ display: 'block', fontSize: 11 }}>
                        {s.feedbackCount} feedback {s.feedbackCount === 1 ? 'response' : 'responses'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {s.role !== 'co-instructor' && deletableStatuses.includes(s.status) ? (
                      <Tooltip title="Delete session">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSessionToDelete(s);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : s.role !== 'co-instructor' ? (
                      <Tooltip title="Active sessions cannot be deleted">
                        <span>
                          <IconButton color="error" size="small" disabled>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Session Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete &quot;{sessionToDelete?.name}&quot; and remove all student assignments. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SessionList;
